/**
 * Coordinates scroll-reveal progress across multiple child sections.
 * Wraps children in a single scroll-tracked container and distributes
 * a normalised 0→1 progress range to each registered ScrollRevealRichText
 * based on its vertical position within the group.
 */
import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { useScroll, MotionValue } from "framer-motion";

interface ChildRegistration {
  id: string;
  element: HTMLElement;
  order: number;
}

interface ChildRange {
  start: number;
  end: number;
}

interface ScrollRevealGroupContextType {
  register: (id: string, element: HTMLElement) => void;
  unregister: (id: string) => void;
  getRange: (id: string) => ChildRange | null;
  scrollYProgress: MotionValue<number>;
}

const ScrollRevealGroupContext =
  createContext<ScrollRevealGroupContextType | null>(null);

export const useScrollRevealGroup = () =>
  useContext(ScrollRevealGroupContext);

interface ScrollRevealGroupProps {
  children: ReactNode;
  className?: string;
}

function getOffsetTop(el: HTMLElement, ancestor: HTMLElement): number {
  let offset = 0;
  let current: HTMLElement | null = el;
  while (current && current !== ancestor) {
    offset += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return offset;
}

export const ScrollRevealGroup = ({
  children,
  className = "",
}: ScrollRevealGroupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const orderCounter = useRef(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 0.66", "end 0.66"],
  });

  const registrationsRef = useRef<ChildRegistration[]>([]);
  const [registrations, setRegistrations] = useState<ChildRegistration[]>([]);
  const rangesRef = useRef<Map<string, ChildRange>>(new Map());
  const rangesVersion = useRef(0);
  const [, setRangesVersion] = useState(0);

  const register = useCallback((id: string, element: HTMLElement) => {
    const order = orderCounter.current++;
    setRegistrations((prev) => {
      if (prev.some((r) => r.id === id)) return prev;
      const next = [...prev, { id, element, order }];
      next.sort((a, b) => a.order - b.order);
      registrationsRef.current = next;
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setRegistrations((prev) => {
      const filtered = prev.filter((r) => r.id !== id);
      if (filtered.length === prev.length) return prev;
      registrationsRef.current = filtered;
      return filtered;
    });
  }, []);

  // Stable measure function that reads from refs, not state
  const measureAndUpdate = useCallback(() => {
    const container = containerRef.current;
    const regs = registrationsRef.current;
    if (!container || regs.length === 0) return;

    const containerHeight = container.scrollHeight;
    if (containerHeight === 0) return;

    const newRanges = new Map<string, ChildRange>();
    let changed = false;
    const oldRanges = rangesRef.current;

    for (const reg of regs) {
      const top = getOffsetTop(reg.element, container);
      const height = reg.element.offsetHeight;
      const start = top / containerHeight;
      const end = (top + height) / containerHeight;
      const old = oldRanges.get(reg.id);
      if (!old || Math.abs(old.start - start) > 0.001 || Math.abs(old.end - end) > 0.001) {
        changed = true;
      }
      newRanges.set(reg.id, { start, end });
    }

    if (changed) {
      rangesRef.current = newRanges;
      rangesVersion.current++;
      setRangesVersion(rangesVersion.current);
    }
  }, []);

  // Recalculate on registration changes and observe resizes
  useEffect(() => {
    measureAndUpdate();

    const container = containerRef.current;
    if (!container) return;

    let raf = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureAndUpdate);
    });
    observer.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [registrations, measureAndUpdate]);

  const getRange = useCallback((id: string): ChildRange | null => {
    return rangesRef.current.get(id) ?? null;
  }, []);

  return (
    <ScrollRevealGroupContext.Provider
      value={{ register, unregister, getRange, scrollYProgress }}
    >
      <div ref={containerRef} className={className}>
        {children}
      </div>
    </ScrollRevealGroupContext.Provider>
  );
};

export default ScrollRevealGroup;
